import { Sizes, TextInput } from '@sede-enterprise/shell-ds-react-framework';
import { companyListAnimation } from 'components/CompanyList/animation-parameters';
import {
  CompanyListScrollContainer,
  CompanyListWrapper,
} from 'components/CompanyList/CompanyList.styled';
import { NoDataFound } from 'components/CompanyList/NoDataFound';
import { CompanyModalWrapper } from 'components/NavBar/components/CompanySelector/CompanySelectorModal/CompanySelectorModal.styled';
import { SiteItem } from 'components/NavBar/components/SiteSelectorMobile/SiteItem';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { useStore } from 'store';
import { SiteEdge } from 'types/site';
import { extractFirstSegmentFromUrl } from 'utils/url';

export const SiteSelectorMobile = ({
  closeModal,
  sites,
  selectedSite,
}: {
  closeModal: () => void;
  sites: Array<SiteEdge['node']>;
  selectedSite: string | undefined;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [siteNameSearch, setSiteNameSearch] = useState('');
  const { company } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [appTheme] = useStore((state) => [state.appTheme]);

  useEffect(() => {
    if (inputRef && sites.length) {
      inputRef?.current?.focus({ preventScroll: true });
    }
  }, [sites.length]);

  const handleSearchChange: React.ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    setSiteNameSearch(event.target.value);
  };

  const displayedSites = useMemo(() => {
    const filtered = siteNameSearch
      ? sites.filter((x) =>
          x.name?.toLowerCase().includes(siteNameSearch?.toLowerCase()),
        )
      : sites;

    return filtered.slice(0, 50);
  }, [siteNameSearch, sites]);

  const handleInputClear = () => setSiteNameSearch('');

  return (
    <CompanyModalWrapper
      open
      onClose={closeModal}
      header={{
        title: 'Select site',
      }}
      closeButton
      contentScrollable={false}
      padding={false}
      centered
      size={Sizes.Small}
      $showMask
      $appTheme={appTheme}
    >
      <div style={{ margin: '16px 16px 0 16px' }}>
        <TextInput
          data-testid="site-selector-modal-search"
          placeholder="Quick search..."
          onChange={handleSearchChange}
          value={siteNameSearch}
          onClear={handleInputClear}
          clearable={true}
          size={Sizes.Large}
          autoFocus
          ref={inputRef}
        />
      </div>
      <CompanyListScrollContainer
        key={siteNameSearch}
        style={{ height: `calc(100vh - 140px)` }}
      >
        <CompanyListWrapper
          variants={companyListAnimation}
          initial="hidden"
          animate="visible"
          data-testid="site-list"
        >
          {!!displayedSites && displayedSites?.length > 0
            ? displayedSites.map((site: SiteEdge['node']) => (
                <SiteItem
                  selectedSite={selectedSite}
                  site={site}
                  key={site.id}
                  onChange={(site) => {
                    const path = generatePath(':company/:route/:site', {
                      company: company || null,
                      route: extractFirstSegmentFromUrl(location.pathname),
                      site,
                    });
                    navigate('/' + path);
                    closeModal();
                  }}
                />
              ))
            : null}
          {!displayedSites?.length ? <NoDataFound /> : null}
        </CompanyListWrapper>
      </CompanyListScrollContainer>
    </CompanyModalWrapper>
  );
};
